<template>
  <div class="expedition-slider">
    <h2 class="title">
      {{ props.title }}
      <span>{{ formattedTime }}</span>
    </h2>

    <div class="slider-container">
      <span class="label" @click="model = props.min.toString()">{{ minLabel }}</span>

      <div class="slider-wrapper">
        <input
          v-model="model"
          :disabled="props.disabled"
          type="range"
          :min="props.min"
          :max="props.max"
          :step="props.step"
          class="slider"
          @input="updateProgress"
        />
        <div class="slider-track"></div>
      </div>

      <span class="label" @click="model = props.max.toString()">{{ maxLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  title: { type: String, default: 'Длительность экспедиции' },
  label: { type: String, default: 'hours' },
  min: { type: Number, default: 3 },
  max: { type: Number, default: 6 },
  step: { type: Number, default: 0.05 },
  disabled: { type: Boolean, default: false }
});

const model = defineModel<string>({ default: '1' });

const progress = ref(0);

const updateProgress = () => {
  progress.value = ((Number(model.value) - props.min) / (props.max - props.min)) * 100;
};

watch(model, updateProgress, { immediate: true });

const formattedTime = computed(() => {
  const totalMinutes = Math.round(Number(model.value) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const s = 0;
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
});

const minLabel = computed(() => `${props.min} ${props.label}`);
const maxLabel = computed(() => `${props.max} ${props.label}`);
</script>

<style scoped>
.expedition-slider {
  text-align: center;
  color: #fff;
  font-family: 'Georgia', serif;
  max-width: 600px;
  width: fit-content;
  min-width: 260px;
}

.title {
  margin-bottom: 10px;
  color: #dedede;
  font-weight: 400;
  font-size: 18px;
}

.slider-container {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 5px 10px;
  position: relative;
  overflow: hidden;
  background: rgba(1, 94, 127, 0.5);
  border-radius: 10px;
}

.label {
  font-weight: 600;
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
  color: #a2a2a2;
  cursor: pointer;
  transition:
    transform 0.12s ease,
    opacity 0.12s ease;

  line-height: 14px;
  &:hover {
    opacity: 1;
  }

  &:active {
    transform: scale(0.97);
  }
}

.slider-wrapper {
  --thumb: 16px;
  --track: 5px;
  position: relative;
  flex: 1;
  height: var(--thumb);
  margin: 0 10px;
}

.slider {
  position: absolute;
  inset: 0;
  width: 100%;
  height: var(--thumb);
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  z-index: 2;
  margin: 0;
}

.slider-track {
  position: absolute;
  left: 0;
  top: 50%;
  height: var(--track);
  transform: translateY(-50%);
  border-radius: 999px;
  background: rgba(4, 26, 39, 0.75);
  z-index: 1;
  pointer-events: none;
  width: 100%;
}

.slider::-webkit-slider-runnable-track {
  height: var(--track);
  background: transparent;
}
.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: var(--thumb);
  height: var(--thumb);
  transform: translateY(-6px);
  background: #3a6b8b;
  transition:
    transform 0.12s ease,
    background 0.12s ease,
    opacity 0.12s ease;

  background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
  border: 1px solid #1e353e;
  box-shadow: 2px 4px 5px rgba(0, 0, 0, 0.25);
  border-radius: 12px;

  opacity: 0.9;
}
.slider:active::-webkit-slider-thumb {
  transform: translateY(-6px) scale(1.1);
}

.slider::-moz-range-track {
  height: 0;
  background: transparent;
  border: 0;
}
.slider::-moz-range-thumb {
  width: var(--thumb);
  height: var(--thumb);
  background: #3a6b8b;
  cursor: grab;
  opacity: 0.9;

  background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
  border: 1px solid #1e353e;
  box-shadow: 2px 4px 5px rgba(0, 0, 0, 0.25);
  border-radius: 12px;
}

.slider:focus-visible::-webkit-slider-thumb {
  outline: none;
  outline-offset: 2px;
}
.slider:hover::-webkit-slider-thumb {
  opacity: 1;
}
.slider:hover::-moz-range-thumb {
  opacity: 1;
}
</style>
